import { initializeApp, getApps } from "firebase/app";

import { getMessaging, getToken, onMessage, isSupported, deleteToken } from "firebase/messaging";

import { onNativeEvent } from "../platform/android/nativeEventBus";
import { NATIVE_EVENTS } from "../platform/android/nativeMethods";
import { Native } from "../platform/android/nativeBridge";

import { registerFcmToken } from "../api/services/registerFcm.service";
import { showGlobalToast } from "./toastService";

import { parseNativeResponse } from "./ParseNativeResponse";

import useAppStore from "../store/useAppStore";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let app = null;
let messaging = null;
let isFirebaseSupported = false;

// Prevent duplicate FCM manager initialization
let isInitialized = false;

let unsubscribeToken = null;
let unsubscribeTriggered = null;
let webMessageUnsubscribe = null;

// Prevent concurrent duplicate token registration
let activeRegisteringToken = null;

const handleTokenRegistration = async (fcmToken, fcmError) => {
  if (fcmError) {
    localStorage.setItem("FCM_ERROR", fcmError);
    return;
  }

  if (!fcmToken || typeof fcmToken !== "string" || fcmToken.trim() === "") {
    localStorage.setItem("FCM_ERROR", "FCM Token missing");
    return;
  }

  const appStoreState = useAppStore.getState();
  const existingToken = appStoreState.fcmToken;
  const userToken = appStoreState.userToken;

  if (!userToken) {
    return;
  }

  if (existingToken === fcmToken || activeRegisteringToken === fcmToken) {
    return;
  }

  try {
    activeRegisteringToken = fcmToken;
    localStorage.setItem("FCM_REGISTERING", "true");
    localStorage.removeItem("FCM_ERROR");
    await registerFcmToken({
      properties: {
        fcm_id: fcmToken,
      },
    });
    const currentState = useAppStore.getState();
    if (currentState.storeFcmToken) {
      currentState.storeFcmToken(fcmToken);
    }
  } catch (err) {
    localStorage.setItem("FCM_ERROR", err?.message || "Failed to send FCM token");
  } finally {
    if (activeRegisteringToken === fcmToken) {
      activeRegisteringToken = null;
    }
    localStorage.removeItem("FCM_REGISTERING");
  }
};

const initFirebaseWeb = async () => {
  try {
    isFirebaseSupported = await isSupported();
    if (!isFirebaseSupported) {
      console.warn("FCM Web: Firebase Messaging is not supported in this browser.");
      return false;
    }
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApps()[0];
    }
    messaging = getMessaging(app);
    return true;
  } catch (error) {
    console.error("FCM Web: Firebase initialization failed:", error);
    return false;
  }
};

const requestWebFCMToken = async () => {
  try {
    if (!messaging || !isFirebaseSupported) {
      return null;
    }
    if (!("Notification" in window)) {
      console.warn("FCM Web: Notifications are not supported in this browser.");
      return null;
    }
    if (!("serviceWorker" in navigator)) {
      console.warn("FCM Web: Service workers are not supported in this browser.");
      return null;
    }
    if (Notification.permission === "denied") {
      showGlobalToast("Please enable notifications in your browser settings.", { type: "error" });
      return null;
    }
    let permission = Notification.permission;
    if (permission !== "granted") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") {
      showGlobalToast("Notification permission is required to receive updates.", { type: "error" });
      return null;
    }
    const params = new URLSearchParams(firebaseConfig).toString();
    await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params}`);
    const registration = await navigator.serviceWorker.ready;
    let token;
    try {
      token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
    } catch (tokenError) {
      const isTokenUpdateFailed =
        tokenError?.code === "messaging/token-update-failed" ||
        tokenError?.message?.includes("messaging/token-update-failed");
      if (isTokenUpdateFailed) {
        await deleteToken(messaging);
        token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration,
        });
      } else {
        throw tokenError;
      }
    }
    if (!token) {
      return null;
    }
    return token;
  } catch (error) {
    console.error("FCM Web: Error getting token:", error);
    return null;
  }
};

export const initFCMManager = async () => {
  if (isInitialized) {
    return;
  }
  isInitialized = true;
  // App native flow
  if (Native.isApp) {
    try {
      unsubscribeToken = onNativeEvent(NATIVE_EVENTS.FCM_TOKEN, async (payload) => {
        const parsedPayload = parseNativeResponse(payload);
        const rawToken = parsedPayload?.token || parsedPayload;
        const fcmError = parsedPayload?.error || null;
        let fcmToken = null;
        if (!fcmError) {
          const parsedToken = parseNativeResponse(rawToken);
          fcmToken = parsedToken?.token || parsedToken || rawToken;
        }
        await handleTokenRegistration(fcmToken, fcmError);
      });

      unsubscribeTriggered = onNativeEvent(NATIVE_EVENTS.FCM_TRIGGERED, (payload) => {
        const rawData = payload?.data || payload;
        const parsedData = parseNativeResponse(rawData);
        window.dispatchEvent(
          new CustomEvent("fcm-triggered", {
            detail: parsedData,
          })
        );
      });
    } catch (err) {
      console.error("FCM Manager: Failed to register Android handlers", err);
      isInitialized = false;
    }
  } else {
    // Web Firebase flow
    const isFirebaseSetup = await initFirebaseWeb();
    if (isFirebaseSetup) {
      webMessageUnsubscribe = onMessage(messaging, (payload) => {
        window.dispatchEvent(
          new CustomEvent("fcm-triggered", {
            detail: payload,
          })
        );
      });
    } else {
      isInitialized = false;
    }
  }
};

export const fetchWebFCMToken = async () => {
  if (Native.isApp) {
    return;
  }
  try {
    const token = await requestWebFCMToken();
    if (token) {
      await handleTokenRegistration(token, null);
    }
  } catch (err) {
    console.error("FCM Manager: Failed to fetch Web FCM token", err);
    await handleTokenRegistration(null, "Failed to fetch Web FCM token");
  }
};

export const destroyFCMManager = () => {
  if (unsubscribeToken) {
    unsubscribeToken();
    unsubscribeToken = null;
  }
  if (unsubscribeTriggered) {
    unsubscribeTriggered();
    unsubscribeTriggered = null;
  }
  if (webMessageUnsubscribe) {
    webMessageUnsubscribe();
    webMessageUnsubscribe = null;
  }
  activeRegisteringToken = null;
  isInitialized = false;
};
